const { useState, useEffect, useMemo, useRef } = React;

function App() {
  const [jobs,    setJobs]    = useState([]);
  const [tab, setTab] = useState(() => localStorage.getItem('jobTrackerActiveTab') || 'dashboard');
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('jobTrackerFilters');
      return saved ? JSON.parse(saved) : { statuses: [], salaries: [], dateRange: 'all', search: '', favoritesOnly: false };
    } catch { return { statuses: [], salaries: [], dateRange: 'all', search: '', favoritesOnly: false }; }
  });
  const [sortOption, setSortOption] = useState(() => localStorage.getItem('jobTrackerSortOption') || 'date-desc');
  const [editing, setEditing] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [theme,   setTheme]   = useState(() => localStorage.getItem('jobTrackerTheme') || 'light');
  const [importStatus, setImportStatus] = useState(null);
  const [csvLimitError, setCsvLimitError] = useState(null);
  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [plan, setPlan] = useState(null);
  const importInputRef = useRef(null);
  const [csvDropdownOpen, setCsvDropdownOpen] = useState(false);
  const csvDropdownRef = useRef(null);
  const [uploadDropdownOpen, setUploadDropdownOpen] = useState(false);
  const uploadDropdownRef = useRef(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkTitle, setBulkTitle] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [mobileOverflowOpen, setMobileOverflowOpen] = useState(false);
  const [dashMobileMoreOpen, setDashMobileMoreOpen] = useState(false);

  useEffect(() => {
    loadJobs()
      .then(data => setJobs(data))
      .catch(error => console.error('Could not load all applications:', error));
    const loadPlan = async () => {
      if (!window.Auth) return;
      const session = await window.Auth.getSession();
      if (!session || !session.user) return;
      posthog.identify(session.user.id, { email: session.user.email });
      const { data } = await window.Auth.getProfile(session.user.id);
      const resolvedPlan = data && data.plan ? String(data.plan).trim().toLowerCase() : 'free';
      setPlan(resolvedPlan);
      posthog.setPersonProperties({ plan: resolvedPlan });
    };
    const refreshPlan = () => {
      if (document.visibilityState === 'visible') loadPlan();
    };
    loadPlan();
    window.addEventListener('focus', loadPlan);
    window.addEventListener('pageshow', loadPlan);
    document.addEventListener('visibilitychange', refreshPlan);
    return () => {
      window.removeEventListener('focus', loadPlan);
      window.removeEventListener('pageshow', loadPlan);
      document.removeEventListener('visibilitychange', refreshPlan);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('jobTrackerActiveTab', tab);
  }, [tab]);

  useEffect(() => {
    localStorage.setItem('jobTrackerFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('jobTrackerSortOption', sortOption);
  }, [sortOption]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('jobTrackerTheme', theme);
  }, [theme]);

  useEffect(() => {
    if (!upgradeMessage) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setUpgradeMessage(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [upgradeMessage]);

  useEffect(() => {
    if (!csvLimitError) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCsvLimitError(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [csvLimitError]);

  useEffect(() => {
    function fixDragGhost(e) {
      const card = e.target.closest && e.target.closest('.draggable-card');
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const ghost = card.cloneNode(true);
      ghost.style.position = 'fixed';
      ghost.style.top = '-9999px';
      ghost.style.left = '-9999px';
      ghost.style.width = rect.width + 'px';
      ghost.style.overflow = 'hidden';
      ghost.style.pointerEvents = 'none';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, e.clientX - rect.left, e.clientY - rect.top);
      requestAnimationFrame(function() { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); });
    }
    document.addEventListener('dragstart', fixDragGhost);
    return () => document.removeEventListener('dragstart', fixDragGhost);
  }, []);

  const jobLimitMessage = `Free accounts can track up to ${FREE_JOB_LIMIT} jobs. Delete a few jobs or upgrade to Pro for unlimited tracking.`;
  const isJobLimitError = (error) => (error?.message || '').includes('free_plan_job_limit_reached');
  const formatCsvFileSize = (bytes) => {
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
  };

  const upsert = async (job) => {
    const isNewJob = !jobs.some(existing => existing.id === job.id);
    if (isNewJob && plan !== 'pro' && jobs.length >= FREE_JOB_LIMIT) {
      posthog.capture('upgrade_prompt_shown', { feature: 'job_limit_on_save' });
      setUpgradeMessage(jobLimitMessage);
      return false;
    }

    const newJob = { ...job, id: job.id || uid() };
    console.log('[AppliClick save] status =', newJob.status, '| id =', newJob.id);
    const { data: savedJob, error } = await saveJob(newJob);
    if (error) {
      if (isJobLimitError(error)) {
        posthog.capture('upgrade_prompt_shown', { feature: 'job_limit_on_save' });
        setUpgradeMessage(jobLimitMessage);
      } else {
        console.error('[AppliClick save] FAILED — full error:', error);
      }
      return false;
    }

    if (isNewJob) {
      posthog.capture('job_added', { status: newJob.status, has_salary: !!newJob.salary, has_contact: !!newJob.contact });
    } else {
      posthog.capture('job_updated', { status: newJob.status });
    }
    setJobs(curr => {
      const persistedJob = savedJob || newJob;
      const i = curr.findIndex(j => j.id === persistedJob.id);
      if (i < 0) return [persistedJob, ...curr];
      const copy = [...curr];
      copy[i] = persistedJob;
      return copy;
    });
    return true;
  };

  const remove = (id) => {
  posthog.capture('job_deleted');
  deleteJob(id);
  setJobs(curr => curr.filter(j => j.id !== id));
};

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setSelectedIds(new Set());
    setBulkAction('');
    setBulkTitle('');
  };

  const isPro = plan === 'pro';
  const renderProBadge = () => plan !== null && plan !== 'pro' ? <span className="pro-badge">Pro</span> : null;
  const requirePro = (msg) => {
    if (isPro) return true;
    posthog.capture('upgrade_prompt_shown', { feature: msg });
    setUpgradeMessage(msg);
    return false;
  };

  const openAddJob = () => {
    if (plan !== 'pro' && jobs.length >= FREE_JOB_LIMIT) {
      posthog.capture('upgrade_prompt_shown', { feature: 'job_limit_on_add' });
      setUpgradeMessage(jobLimitMessage);
      return;
    }
    setAdding(true);
  };

  const applyBulkAction = async () => {
    if (!requirePro('Bulk editing is a Pro feature.')) return;
    if (!selectedIds.size || !bulkAction) return;
    const ids = [...selectedIds];
    const n = ids.length;
    const label = n === 1 ? '1 job' : `${n} jobs`;

    if (bulkAction === 'favorite') {
      if (!confirm(`Mark ${label} as favorite?`)) return;
      await bulkSetFavorite(ids, true);
      setJobs(curr => curr.map(j => selectedIds.has(j.id) ? { ...j, favorite: true } : j));
      posthog.capture('bulk_action_applied', { action: 'favorite', count: ids.length });
      exitBulkMode();
    } else if (bulkAction === 'unfavorite') {
      if (!confirm(`Remove favorite from ${label}?`)) return;
      await bulkSetFavorite(ids, false);
      setJobs(curr => curr.map(j => selectedIds.has(j.id) ? { ...j, favorite: false } : j));
      posthog.capture('bulk_action_applied', { action: 'unfavorite', count: ids.length });
      exitBulkMode();
    } else if (bulkAction === 'title') {
      const trimmed = bulkTitle.trim();
      if (!trimmed) return;
      if (!confirm(`Set job title to "${trimmed}" for ${label}?`)) return;
      await bulkSetTitle(ids, trimmed);
      setJobs(curr => curr.map(j => selectedIds.has(j.id) ? { ...j, title: trimmed } : j));
      posthog.capture('bulk_action_applied', { action: 'title', count: ids.length });
      exitBulkMode();
    } else if (bulkAction === 'delete') {
      if (!confirm(`Delete ${label}? This cannot be undone.`)) return;
      await deleteJobs(ids);
      setJobs(curr => curr.filter(j => !selectedIds.has(j.id)));
      posthog.capture('bulk_action_applied', { action: 'delete', count: ids.length });
      exitBulkMode();
    }
  };

  const downloadCSV = () => {
    if (!requirePro('CSV export is a Pro feature.')) return;
    const headers = ['Company', 'Title', 'Date', 'Status', 'Salary', 'Point of Contact Name', 'Point of Contact Email', 'Point of Contact LinkedIn', 'Sent Email', 'Sent Email Date', 'Link', 'Notes', 'Favorite'];
    const rows = jobs.map(j => [j.company, j.title, j.date, j.status, j.salary, j.contactName || j.contact || '', j.contactEmail || '', j.contactLinkedIn || '', j.sentEmail ? 'true' : 'false', j.sentEmailDate || '', j.link, j.notes, j.favorite ? 'true' : 'false']
      // Prepend ' to values starting with =+-@ to prevent CSV/Excel formula injection attacks.
      .map(v => { const s = /^[=+\-@]/.test(v || '') ? `'${v}` : (v || ''); return `"${s.replace(/"/g, '""')}"` ; }));
    const csv = '﻿' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
      download: 'jobs.csv',
    });
    a.click();
    URL.revokeObjectURL(a.href);
    posthog.capture('csv_exported', { job_count: jobs.length });
    setCsvDropdownOpen(false);
  };

  const downloadCSVTemplate = () => {
    const headers = ['Company', 'Title', 'Date', 'Status', 'Salary', 'Contact', 'Contact Email', 'Contact LinkedIn', 'Sent Email', 'Sent Email Date', 'Link', 'Notes', 'Favorite'];
    const examples = [
      ['Acme Corp', 'Product Designer', '2026-01-15', 'applied', '$150,000 - $180,000', 'Jane Smith', 'jane@acme.com', 'linkedin.com/in/janesmith', 'false', '', 'https://example.com', 'Heard back within a week', 'false'],
      ['Globex', 'Senior UX Designer', '2026-01-20', 'interview', '$160,000', 'Tom Lee', 'tom@globex.com', '', 'true', '2026-01-21', '', 'Phone screen scheduled', 'false'],
      ['Initech', 'UX Lead', '2025-12-01', 'ghosted', '', '', '', '', 'false', '', '', 'Applied 6 weeks ago, no response', 'false'],
    ].map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(','));
    const csv = '﻿' + [headers.join(','), ...examples].join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
      download: 'jobs-template.csv',
    });
    a.click();
    URL.revokeObjectURL(a.href);
    setCsvDropdownOpen(false);
  };

  useEffect(() => {
    if (!csvDropdownOpen) return;
    const handler = (e) => {
      if (csvDropdownRef.current && !csvDropdownRef.current.contains(e.target)) {
        setCsvDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [csvDropdownOpen]);

  useEffect(() => {
    if (!uploadDropdownOpen) return;
    const handler = (e) => {
      if (uploadDropdownRef.current && !uploadDropdownRef.current.contains(e.target)) {
        setUploadDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [uploadDropdownOpen]);

  const importCSV = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    setUploadDropdownOpen(false);
    if (!file) return;
    if (!requirePro('CSV import is a Pro feature.')) return;

    if (file.size > MAX_CSV_FILE_BYTES) {
      setImportStatus(null);
      setCsvLimitError({
        title: 'File too large',
        message: `Your file is ${formatCsvFileSize(file.size)}. The CSV import limit is 1 MB. Please upload a smaller file.`,
      });
      return;
    }

    const { data: { user }, error: userError } = await supabaseClient.auth.getUser();
    if (userError || !user) { window.location.href = 'login.html'; return; }

    const rows = [];
    let exceededRowLimit = false;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      step: ({ data: row }, parser) => {
        rows.push(row);
        if (rows.length > MAX_CSV_IMPORT_ROWS) {
          exceededRowLimit = true;
          parser.abort();
        }
      },
      complete: async () => {
        if (exceededRowLimit) {
          setImportStatus(null);
          setCsvLimitError({
            title: 'Too many rows',
            message: `Your CSV has more than ${MAX_CSV_IMPORT_ROWS.toLocaleString()} rows. The limit is ${MAX_CSV_IMPORT_ROWS.toLocaleString()} rows. Please split it into smaller files and try again.`,
          });
          return;
        }

        if (!rows.length) {
          setImportStatus({ ok: false, msg: 'The CSV file is empty.' });
          return;
        }

        // Normalise header keys: lowercase, strip spaces/underscores
        const norm = (s) => (s || '').toLowerCase().replace(/[\s_]/g, '');

        // Parse a date string into { iso, invalid }. `iso` is a YYYY-MM-DD
        // string or null (never ""); `invalid` is true only when a non-empty
        // value could not be parsed into a real calendar date.
        const parseDateField = (raw) => {
          const s = (raw || '').trim();
          if (!s) return { iso: null, invalid: false };

          let y, m, d;
          const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
          const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
          const mdy2 = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s);
          if (iso)      { y = +iso[1];  m = +iso[2];  d = +iso[3]; }
          else if (mdy) { y = +mdy[3];  m = +mdy[1];  d = +mdy[2]; }
          else if (mdy2){ y = +mdy2[3]; m = +mdy2[1]; d = +mdy2[2]; }
          else return { iso: null, invalid: true };

          // Reject dates that don't round-trip (e.g. 2026-02-30, 13/40/2026).
          const dt = new Date(Date.UTC(y, m - 1, d));
          if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
            return { iso: null, invalid: true };
          }

          const pad = (n) => String(n).padStart(2, '0');
          return { iso: `${y}-${pad(m)}-${pad(d)}`, invalid: false };
        };

        // Keys must match STATUSES ids in constants.js: applied, interview, yes, ghosted, no
        const STATUS_MAP_IMPORT = {
          applied:   ['applied','waiting','submitted','pending','saved','followup'],
          interview: ['interview','interviewing','phonescreen','phoneinterview','recruiterscreen','screening','hiringmanager','portfolioreview','assignment','takehome','finalround','onsite'],
          yes:       ['offer','offered','accepted','hired'],
          ghosted:   ['ghosted','inactive','abandoned','stale','noresponse','neverheardback','dead','unresponsive'],
          no:        ['no','rejected','rejection','reject','declined','denied','passed','notselected','closed','archived','withdrawn'],
        };
        const normalizeStatus = (raw) => {
          // strip spaces, underscores, and hyphens before matching
          const key = (raw || '').toLowerCase().replace(/[\s_-]/g, '');
          for (const [status, aliases] of Object.entries(STATUS_MAP_IMPORT)) {
            if (aliases.includes(key)) return status;
          }
          return 'applied';
        };

        // Map each row to the jobs table fields. Rows with an unparseable
        // Date Applied value are skipped (never sent to the db as "") and
        // reported back to the user with their original CSV row number.
        const dateErrors = [];
        const mapped = [];
        rows.forEach((row, i) => {
          const r = {};
          for (const k of Object.keys(row)) r[norm(k)] = (row[k] || '').trim();

          const company = r['company'] || r['companyname'] || '';
          const title   = r['title']   || r['jobtitle'] || r['role'] || '';
          if (!company && !title) return; // fully blank row

          const { iso: dateISO, invalid: dateInvalid } =
            parseDateField(r['date'] || r['dateapplied'] || r['applied'] || '');
          if (dateInvalid) {
            dateErrors.push(`Row ${i + 1} has an invalid Date Applied value.`);
            return;
          }
          const { iso: sentEmailDateISO } = parseDateField(r['sentemaildate'] || '');

          mapped.push({
            id:       uid(),
            user_id:  user.id,
            company,
            title,
            date:     dateISO,
            status:   normalizeStatus(r['status']),
            salary:   (r['salary']  || r['salaryrange']                      || '').trim(),
            contact:          (r['contact'] || r['contactname'] || r['pointofcontact'] || '').trim(),
            contact_email:    r['contactemail']    || '',
            contact_linkedin: r['contactlinkedin'] || '',
            sent_email:       ['true', '1', 'yes'].includes((r['sentemail'] || '').toLowerCase()),
            sent_email_date:  sentEmailDateISO,
            link:     (r['link']    || r['url'] || r['listinglink']          || '').trim(),
            notes:    (r['notes']                                             || '').trim(),
            favorite: ['true', '1', 'yes', 'favorite'].includes((r['favorite'] || '').toLowerCase()),
          });
        });

        if (!mapped.length) {
          setImportStatus({
            ok: false,
            msg: dateErrors.length ? dateErrors[0] : 'No valid rows found. Every row needs a company and job title.',
          });
          return;
        }

        if (mapped.some(r => !r.company.trim() || !r.title.trim())) {
          setImportStatus({ ok: false, msg: 'Import stopped. Every CSV row needs both a company and job title.' });
          return;
        }

        const { error } = await supabaseClient.from('jobs').insert(mapped);
        if (error) {
          const isDateError = (error?.message || '').includes('invalid_job_date')
            || (error?.message || '').toLowerCase().includes('type date');
          const msg = isJobLimitError(error)
            ? jobLimitMessage
            : isDateError
              ? 'Import failed: one or more rows had an invalid Date Applied value. Please check the date column and try again.'
              : `Import failed: ${error.message}`;
          setImportStatus({ ok: false, msg });
        } else {
          try {
            const fresh = await loadJobs();
            setJobs(fresh);
            posthog.capture('csv_imported', { count: mapped.length });
            let msg = `Imported ${mapped.length} job${mapped.length !== 1 ? 's' : ''}.`;
            if (dateErrors.length) {
              msg += ` Skipped ${dateErrors.length} row${dateErrors.length !== 1 ? 's' : ''}: ${dateErrors.join(' ')}`;
            }
            setImportStatus({ ok: true, msg });
          } catch (loadError) {
            console.error('Could not reload all applications after CSV import:', loadError);
            setImportStatus({ ok: false, msg: 'Jobs were imported, but the full list could not be refreshed. Please reload the page.' });
          }
        }
        setTimeout(() => setImportStatus(null), 5000);
      },
      error: () => {
        setImportStatus({ ok: false, msg: 'Could not read the file. Make sure it is a valid CSV.' });
        setTimeout(() => setImportStatus(null), 5000);
      },
    });
  };

  const today = todayISO();

  // Filtered job list used by all views
  const filtered = useMemo(() => {
    const maxAgeDays = (() => {
      if (filters.dateRange === '7') return 7;
      if (filters.dateRange === '30') return 30;
      return null;
    })();
    return jobs.filter(j => {
      if (filters.favoritesOnly && !j.favorite) return false;
      if (filters.statuses.length && !filters.statuses.includes(j.status)) return false;
      if (filters.salaries.length) {
        const bands = salaryToBands(j.salary);
        if (!bands.some(band => filters.salaries.includes(band))) return false;
      }
      if (filters.dateRange === 'today' && j.date !== today) return false;
      if (maxAgeDays != null && j.date) {
        const ageDays = daysBetween(j.date, today);
        if (ageDays != null && ageDays > maxAgeDays) return false;
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (
          !(j.company || '').toLowerCase().includes(q) &&
          !(j.title   || '').toLowerCase().includes(q) &&
          !(j.contactName || j.contact || '').toLowerCase().includes(q) &&
          !(j.contactEmail || '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [jobs, filters, today]);

  const allSelected = filtered.length > 0 && filtered.every(j => selectedIds.has(j.id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filtered.map(j => j.id)));
  };

  // Reminder lists for dashboard + tab badge
  const reminders = useMemo(() => {
    const stale = [], followUp = [];
    for (const j of jobs) {
      if (j.status !== 'applied' || !j.date) continue;
      const d = daysBetween(j.date, today);
      if (d != null && d >= STALE_DAYS && d < 40) stale.push({ ...j, daysOld: d });
      if ((j.contactName || j.contact || j.contactEmail) && d != null && d >= FOLLOWUP_DAYS && d < 40) followUp.push({ ...j, daysOld: d });
    }
    return {
      stale:    stale.sort((a, b) => b.daysOld - a.daysOld),
      followUp: followUp.sort((a, b) => b.daysOld - a.daysOld),
    };
  }, [jobs, today]);

  // Stats for header tiles + dashboard chart
  const stats = useMemo(() => {
    const total     = jobs.length;
    const responded = jobs.filter(j => ['interview', 'yes', 'no'].includes(j.status)).length;
    const respRate  = total ? Math.round(responded / total * 100) : 0;

    const weeks = [0, 0, 0, 0];
    for (const j of jobs) {
      if (!j.date) continue;
      const d = daysBetween(j.date, today);
      if (d == null || d < 0) continue;
      const wIdx = Math.floor(d / 7);
      if (wIdx < 4) weeks[wIdx]++;
    }
    const avgPerWeek = Math.round(weeks.reduce((a, b) => a + b, 0) / 4);

    const salaryMidpoints = jobs
      .map(j => parseSalaryValue(j.salary))
      .filter(value => value != null);
    const avgSalary = salaryMidpoints.length
      ? Math.round(salaryMidpoints.reduce((sum, value) => sum + value, 0) / salaryMidpoints.length)
      : null;

    const activeCount = jobs.filter(j => j.status === 'applied' || j.status === 'interview').length;

    return { total, respRate, responded, avgPerWeek, weeks, avgSalary, activeCount };
  }, [jobs, today]);

  const activeFilterCount =
    (filters.favoritesOnly ? 1 : 0) +
    filters.statuses.length +
    filters.salaries.length +
    (filters.dateRange !== 'all' ? 1 : 0);

  return (
    <div className="wrap" data-plan={isPro ? 'pro' : 'free'}>
      {/* Header */}
      <div className="header">
        <div className="brand">
          <img src={theme === 'dark' ? 'assets/images/AppliClick Logo and Icon WHITE.svg' : 'assets/images/AppliClick-Logo-icon.svg'} alt="AppliClick" height="28" className="brand-logo" />
        </div>
        <div className="header-actions">
          <a className="profile-icon-btn" href="profile.html" aria-label="Profile and settings" title="Profile and settings">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <circle cx="8.5" cy="5.2" r="3.1" stroke="currentColor" strokeWidth="1.6"/>
              <path d="M2.7 15c.8-3 3-4.6 5.8-4.6s5 1.6 5.8 4.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-row">
        <div className="tabs">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'pipeline',  label: 'Pipeline' },
            { id: 'table',     label: 'Table' },
            { id: 'cards',     label: 'Cards' },
          ].map(it => (
            <button key={it.id} className={`tab ${tab === it.id ? 'active' : ''}`} onClick={() => setTab(it.id)}>
              {it.label}
            </button>
          ))}
        </div>

        <div className="top-actions top-actions-hide-mobile">
          {bulkMode ? (
            <>
              <span className="bulk-count">{selectedIds.size} selected</span>
              <button className="sketch-btn" type="button" onClick={exitBulkMode} style={{ background: '#DC2626', borderColor: '#DC2626', color: '#fff' }}>Cancel</button>
              <button className="sketch-btn" type="button" onClick={toggleSelectAll}>
                {allSelected ? 'Deselect all' : `Select all (${filtered.length})`}
              </button>
              <select
                className="bulk-action-select"
                value={bulkAction}
                onChange={e => { setBulkAction(e.target.value); setBulkTitle(''); }}
              >
                <option value="">Choose action…</option>
                <option value="favorite">Mark as favorite</option>
                <option value="unfavorite">Unmark as favorite</option>
                <option value="title">Edit job title</option>
                <option value="delete">Delete selected</option>
              </select>
              {bulkAction === 'title' && (
                <input
                  className="bulk-title-input"
                  type="text"
                  placeholder="New job title…"
                  value={bulkTitle}
                  onChange={e => setBulkTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyBulkAction()}
                  autoFocus
                />
              )}
              {bulkAction && (
                <button
                  className="sketch-btn primary"
                  type="button"
                  onClick={applyBulkAction}
                  disabled={selectedIds.size === 0 || (bulkAction === 'title' && !bulkTitle.trim())}
                  style={bulkAction === 'delete' ? { background: '#DC2626', borderColor: '#DC2626' } : {}}
                >
                  Apply
                </button>
              )}
            </>
          ) : (
            <>
              {tab !== 'dashboard' && (
                <button className="bulk-btn" type="button" onClick={() => requirePro('Bulk editing is a Pro feature.') && setBulkMode(true)}>
                  Bulk edit {renderProBadge()}
                </button>
              )}
              <div className="csv-dropdown-wrap" ref={csvDropdownRef}>
                <button className="csv-btn" type="button" onClick={() => setCsvDropdownOpen(o => !o)} aria-label="Download CSV">
                  <span className="material-icons" aria-hidden="true">download</span>
                  {renderProBadge()}
                  <span className="csv-tooltip">download .csv</span>
                </button>
                {csvDropdownOpen && (
                  <div className="csv-dropdown">
                    <button type="button" onClick={downloadCSVTemplate}>Download a CSV Template</button>
                    <button type="button" onClick={downloadCSV}>Download Your Job Data</button>
                  </div>
                )}
              </div>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={importCSV}
                aria-label="Import CSV"
              />
              <div className="csv-dropdown-wrap" ref={uploadDropdownRef}>
                <button className="csv-btn" type="button" onClick={() => setUploadDropdownOpen(o => !o)} aria-label="Import CSV">
                  <span className="material-icons" aria-hidden="true">upload</span>
                  {renderProBadge()}
                  <span className="csv-tooltip">import .csv</span>
                </button>
                {uploadDropdownOpen && (
                  <div className="csv-dropdown">
                    <button type="button" onClick={() => requirePro('CSV import is a Pro feature.') && importInputRef.current.click()}>
                      <span className="csv-dropdown-title">Upload CSV</span>
                      <span className="csv-dropdown-hint">Import jobs from a CSV file. Fields like Company, Role, Status, Salary, and Notes are mapped automatically. Valid statuses: applied, interview, offer, ghosted, rejected.</span>
                    </button>
                  </div>
                )}
              </div>
              {importStatus && (
                <span className={`import-status ${importStatus.ok ? 'ok' : 'err'}`}>
                  {importStatus.msg}
                </span>
              )}
              <button type="button" className="theme-toggle" aria-label="Toggle dark mode" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
                <span className={`theme-toggle-opt ${theme === 'light' ? 'active' : ''}`}>Light</span>
                <span className={`theme-toggle-opt ${theme === 'dark' ? 'active' : ''}`}>Dark</span>
              </button>
              <button className="candy-btn add" type="button" onClick={openAddJob}>
                + Add job
              </button>
            </>
          )}
        </div>
      </div>

      {upgradeMessage && (
        <UpgradeModal
          message={upgradeMessage}
          onClose={() => setUpgradeMessage(null)}
        />
      )}

      {csvLimitError && (
        <CsvLimitModal
          title={csvLimitError.title}
          message={csvLimitError.message}
          onClose={() => setCsvLimitError(null)}
        />
      )}

      {tab !== 'dashboard' && (
        <div className="desktop-filters">
          <Filters
            filters={filters}
            setFilters={setFilters}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </div>
      )}

      {tab !== 'dashboard' && (
        <div className="mobile-toolbar">
          {bulkMode ? (
            <div className="mobile-bulk-bar">
              <div className="mobile-bulk-top">
                <span className="bulk-count">{selectedIds.size} selected</span>
                <button className="sketch-btn" type="button" onClick={toggleSelectAll}>
                  {allSelected ? 'Deselect all' : `Select all (${filtered.length})`}
                </button>
                <button className="sketch-btn" type="button" onClick={exitBulkMode}>Cancel</button>
              </div>
              <select className="bulk-action-select" style={{ width: '100%' }} value={bulkAction}
                onChange={e => { setBulkAction(e.target.value); setBulkTitle(''); }}>
                <option value="">Choose action…</option>
                <option value="favorite">Mark as favorite</option>
                <option value="unfavorite">Unmark as favorite</option>
                <option value="title">Edit job title</option>
                <option value="delete">Delete selected</option>
              </select>
              {bulkAction === 'title' && (
                <input className="sketch-input" type="text" placeholder="New job title…"
                  value={bulkTitle} onChange={e => setBulkTitle(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyBulkAction()} autoFocus />
              )}
              {bulkAction && (
                <button className="candy-btn add" type="button" onClick={applyBulkAction}
                  disabled={selectedIds.size === 0 || (bulkAction === 'title' && !bulkTitle.trim())}
                  style={Object.assign({ width: '100%' }, bulkAction === 'delete' ? { background: '#DC2626', borderColor: '#DC2626' } : {})}>
                  Apply
                </button>
              )}
            </div>
          ) : (
            <>
              <button className="candy-btn add" style={{ width: '100%', justifyContent: 'center' }}
                type="button" onClick={openAddJob}>+ Add job</button>
              <div className="mobile-search-row">
                <div className="mobile-search-wrap">
                  <input className="sketch-input mobile-search-input" placeholder="Search jobs…"
                    value={filters.search}
                    onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
                  {filters.search && (
                    <button type="button" className="mobile-search-clear"
                      onClick={() => setFilters(f => ({ ...f, search: '' }))}>×</button>
                  )}
                </div>
                <button
                  type="button"
                  className={`mobile-action-btn mobile-menu-btn${(mobileOverflowOpen || activeFilterCount > 0) ? ' has-active' : ''}`}
                  aria-label="Open actions menu"
                  onClick={() => { setMobileOverflowOpen(v => !v); setMobileFilterOpen(false); setMobileSortOpen(false); }}
                >
                  <span className="material-icons" style={{ fontSize: 20 }}>menu</span>
                </button>
              </div>
              {mobileSortOpen && (
                <div className="mobile-sort-panel">
                  {SORT_OPTIONS.map(opt => (
                    <button key={opt.value}
                      className={`mobile-sort-opt${sortOption === opt.value ? ' active' : ''}`}
                      type="button"
                      onClick={() => { setSortOption(opt.value); setMobileSortOpen(false); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {mobileOverflowOpen && (
                <div className="mobile-overflow-menu">
                  <button className={`mobile-overflow-item${activeFilterCount > 0 ? ' has-active' : ''}`} type="button"
                    onClick={() => { setMobileFilterOpen(true); setMobileOverflowOpen(false); }}>
                    Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                  </button>
                  <button className="mobile-overflow-item" type="button"
                    onClick={() => { setMobileSortOpen(v => !v); setMobileOverflowOpen(false); }}>
                    Sort
                  </button>
                  <button className="mobile-overflow-item" type="button"
                    onClick={() => { setMobileOverflowOpen(false); requirePro('Bulk editing is a Pro feature.') && setBulkMode(true); }}>
                    Bulk edit {renderProBadge()}
                  </button>
                  <button className="mobile-overflow-item" type="button"
                    onClick={() => { downloadCSV(); setMobileOverflowOpen(false); }}>
                    Download CSV {renderProBadge()}
                  </button>
                  <button className="mobile-overflow-item" type="button"
                    onClick={() => { if (requirePro('CSV import is a Pro feature.')) importInputRef.current.click(); setMobileOverflowOpen(false); }}>
                    Import CSV {renderProBadge()}
                  </button>
                  <div className="mobile-overflow-item mobile-theme-row">
                    <span>Theme</span>
                    <button className="theme-toggle" type="button"
                      onClick={() => { setTheme(theme === 'light' ? 'dark' : 'light'); setMobileOverflowOpen(false); }}>
                      <span className={`theme-toggle-opt ${theme === 'light' ? 'active' : ''}`}>Light</span>
                      <span className={`theme-toggle-opt ${theme === 'dark' ? 'active' : ''}`}>Dark</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'dashboard' && (
        <div className="mobile-toolbar">
          <div className="mobile-action-row">
            <button className="candy-btn add" style={{ flex: 1, justifyContent: 'center' }}
              type="button" onClick={openAddJob}>+ Add job</button>
            <button className="mobile-action-btn" style={{ flex: '0 0 auto', padding: '0 18px' }}
              type="button" onClick={() => setDashMobileMoreOpen(v => !v)}>More</button>
          </div>
          {dashMobileMoreOpen && (
            <div className="mobile-overflow-menu">
              <button className="mobile-overflow-item" type="button"
                onClick={() => { downloadCSV(); setDashMobileMoreOpen(false); }}>
                Export CSV {renderProBadge()}
              </button>
              <button className="mobile-overflow-item" type="button"
                onClick={() => { if (requirePro('CSV import is a Pro feature.')) importInputRef.current.click(); setDashMobileMoreOpen(false); }}>
                Import CSV {renderProBadge()}
              </button>
              <div className="mobile-overflow-item mobile-theme-row">
                <span>Theme</span>
                <button className="theme-toggle" type="button"
                  onClick={() => { setTheme(theme === 'light' ? 'dark' : 'light'); setDashMobileMoreOpen(false); }}>
                  <span className={`theme-toggle-opt ${theme === 'light' ? 'active' : ''}`}>Light</span>
                  <span className={`theme-toggle-opt ${theme === 'dark' ? 'active' : ''}`}>Dark</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {importStatus && (
        <div className={`import-status mobile-import-status ${importStatus.ok ? 'ok' : 'err'}`}>
          {importStatus.msg}
        </div>
      )}

      {tab === 'dashboard' && <Stats stats={stats} />}

      <main>
        {tab === 'dashboard' && <Dashboard jobs={jobs} stats={stats} reminders={reminders} onOpen={setEditing} upsert={upsert} onAdd={openAddJob} onImportCSV={() => requirePro('CSV import is a Pro feature.') && importInputRef.current.click()} onDownloadTemplate={downloadCSVTemplate} />}
        {tab === 'pipeline'  && <Pipeline  jobs={filtered} reminders={reminders} onOpen={setEditing} upsert={upsert} onAdd={openAddJob} sortOption={sortOption} bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={toggleSelect} />}
        {tab === 'table'     && <TableView jobs={filtered} onOpen={setEditing} upsert={upsert} sortOption={sortOption} setSortOption={setSortOption} bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={toggleSelect} onAdd={openAddJob} onImportCSV={() => requirePro('CSV import is a Pro feature.') && importInputRef.current.click()} onDownloadTemplate={downloadCSVTemplate} renderProBadge={renderProBadge} />}
        {tab === 'cards'     && <CardList  jobs={filtered} onOpen={setEditing} upsert={upsert} sortOption={sortOption} bulkMode={bulkMode} selectedIds={selectedIds} onToggleSelect={toggleSelect} onAdd={openAddJob} onImportCSV={() => requirePro('CSV import is a Pro feature.') && importInputRef.current.click()} onDownloadTemplate={downloadCSVTemplate} renderProBadge={renderProBadge} />}
      </main>

      {mobileFilterOpen && tab !== 'dashboard' && (
        <>
          <div className="mobile-sheet-scrim" onClick={() => setMobileFilterOpen(false)} />
          <div className="mobile-filter-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-head">
              <span className="mobile-sheet-title">Filters</span>
              <button className="mobile-sheet-done" type="button" onClick={() => setMobileFilterOpen(false)}>Done</button>
            </div>
            <div className="mobile-sheet-body">
              <div className="mobile-filter-group">
                <div className="mobile-filter-label">Favorites</div>
                <div className="mobile-filter-pills">
                  <button className={`filter-pill${filters.favoritesOnly ? ' on' : ''}`} type="button"
                    onClick={() => setFilters(f => ({ ...f, favoritesOnly: !f.favoritesOnly }))}>
                    <span className="material-icons favorite-heart" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }}>auto_awesome</span>Favorites only
                  </button>
                </div>
              </div>
              <div className="mobile-filter-group">
                <div className="mobile-filter-label">Status</div>
                <div className="mobile-filter-pills">
                  {STATUSES.map(s => (
                    <button key={s.id}
                      className={`filter-pill${filters.statuses.includes(s.id) ? ' on' : ''}`}
                      type="button"
                      onClick={() => setFilters(f => ({
                        ...f,
                        statuses: f.statuses.includes(s.id)
                          ? f.statuses.filter(x => x !== s.id)
                          : [...f.statuses, s.id]
                      }))}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mobile-filter-group">
                <div className="mobile-filter-label">Date</div>
                <div className="mobile-filter-pills">
                  {[
                    { value: 'today', label: 'Today' },
                    { value: '7', label: 'Last 7d' },
                    { value: '30', label: 'Last 30d' },
                    { value: 'all', label: 'All time' },
                  ].map(opt => (
                    <button key={opt.value}
                      className={`filter-pill${filters.dateRange === opt.value ? ' on' : ''}`}
                      type="button"
                      onClick={() => setFilters(f => ({ ...f, dateRange: opt.value }))}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mobile-filter-group">
                <div className="mobile-filter-label">Salary</div>
                <div className="mobile-filter-pills">
                  {SALARY_BANDS.map(b => (
                    <button key={b}
                      className={`filter-pill${filters.salaries.includes(b) ? ' on' : ''}`}
                      type="button"
                      onClick={() => setFilters(f => ({
                        ...f,
                        salaries: f.salaries.includes(b)
                          ? f.salaries.filter(x => x !== b)
                          : [...f.salaries, b]
                      }))}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button className="clear-filters" type="button"
                  onClick={() => {
                    setFilters({ statuses: [], salaries: [], dateRange: 'all', search: filters.search, favoritesOnly: false });
                    setMobileFilterOpen(false);
                  }}>
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {adding && (
        <AddJobModal
          jobs={jobs}
          onClose={() => setAdding(false)}
          onSave={async (job) => { if (await upsert(job) !== false) setAdding(false); }}
        />
      )}

      {editing && (
        <DetailDrawer
          job={editing}
          onClose={() => setEditing(null)}
          onSave={async (j) => { if (await upsert(j) !== false) setEditing(null); }}
          onDelete={(id) => { remove(id); setEditing(null); }}
        />
      )}

      <nav className="mob-bottom-nav" aria-label="Main navigation">
        {[
          { id: 'dashboard', label: 'Home',     icon: 'home' },
          { id: 'pipeline',  label: 'Pipeline', icon: 'timeline' },
          { id: 'table',     label: 'Table',    icon: 'table_view' },
          { id: 'cards',     label: 'Cards',    icon: 'grid_view' },
        ].map(it => (
          <button key={it.id} className={`mob-nav-btn${tab === it.id ? ' active' : ''}`}
            onClick={() => setTab(it.id)} aria-label={it.label}>
            <span className="mob-nav-icon-wrap">
              <span className="material-icons">{it.icon}</span>
            </span>
            <span className="mob-nav-label">{it.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function AddJobModal({ jobs, onClose, onSave }) {
  const [form, setForm] = useState({
    company: '',
    title: '',
    date: todayISO(),
    status: 'applied',
    salary: '',
    contactName: '',
    contactEmail: '',
    contactLinkedIn: '',
    sentEmail: false,
    sentEmailDate: '',
    notes: '',
    link: '',
    favorite: false,
  });
  const linkInputRef = useRef(null);
  const [autofillStatus, setAutofillStatus] = useState(null);

  useEffect(() => {
    if (window.matchMedia('(min-width: 641px)').matches) {
      linkInputRef.current?.focus();
    }
  }, []);

  const update = (key, value) => {
    setForm(curr => ({ ...curr, [key]: value }));
  };

  const triggerAutofill = async (url) => {
    if (!url || !url.startsWith('http')) return;
    const isLinkedIn = /linkedin\.com/i.test(url);
    setAutofillStatus('loading');
    const result = await fetchJobPostingDetails(url);
    if (!result) { setAutofillStatus(null); return; }
    if (result.corsBlocked || result.empty || result.error) {
      setAutofillStatus(isLinkedIn ? 'linkedin' : 'cors');
      return;
    }
    const titleWasBlank   = !form.title.trim();
    const companyWasBlank = !form.company.trim();
    const titleFilled   = titleWasBlank   && !!result.title;
    const companyFilled = companyWasBlank && !!result.company;
    const salaryFilled  = !form.salary.trim() && !!result.salary;
    const anyFilled = titleFilled || companyFilled || salaryFilled;
    setForm(curr => ({
      ...curr,
      company: curr.company.trim() ? curr.company : (result.company || curr.company),
      title:   curr.title.trim()   ? curr.title   : (result.title   || curr.title),
      salary:  curr.salary.trim()  ? curr.salary  : (result.salary  ? formatSalaryValue(result.salary) : curr.salary),
    }));
    if (!anyFilled) {
      setAutofillStatus('cors');
    } else if (titleWasBlank && !titleFilled) {
      setAutofillStatus('partial-notitle');
    } else if (companyWasBlank && !companyFilled) {
      setAutofillStatus('partial');
    } else {
      setAutofillStatus('done');
    }
  };

  const titleSuggestions = [...new Set(
    jobs
      .map(job => job.title)
      .filter(Boolean)
      .map(title => title.trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  const submit = (event) => {
    event.preventDefault();
    if (!form.company.trim() || !form.title.trim()) return;

    onSave({
      id: uid(),
      company: form.company.trim(),
      title: form.title.trim(),
      date: form.date,
      status: form.status,
      salary: form.salary.trim(),
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim(),
      contactLinkedIn: form.contactLinkedIn.trim(),
      sentEmail: form.sentEmail,
      sentEmailDate: form.sentEmailDate,
      notes: form.notes.trim(),
      link: form.link.trim(),
      favorite: form.favorite,
    });
  };

  return (
    <div className="scrim" onClick={onClose}>
      <section className="sketch-box add-modal" onClick={(event) => event.stopPropagation()}>
        <div className="add-modal-head">
          <div>
            <h2>Add a Job</h2>
          </div>
          <button className="add-modal-close-x" type="button" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form id="add-job-form" className="add-job-form" onSubmit={submit}>
          <div className="row2">
            <div className="field">
              <label>Company</label>
              <input className="sketch-input" value={form.company} onChange={(event) => update('company', event.target.value)} required />
            </div>
            <div className="field">
              <label>Job title</label>
              <input
                className="sketch-input"
                list="job-title-suggestions"
                value={form.title}
                onChange={(event) => update('title', event.target.value)}
                required
              />
              <datalist id="job-title-suggestions">
                {titleSuggestions.map(title => (
                  <option key={title} value={title} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="row2">
            <div className="field modal-date-field">
              <label>Date applied</label>
              <div className="modal-date-clip w-full max-w-full min-w-0 overflow-hidden">
                <input className="sketch-input modal-date-input w-full max-w-full min-w-0 box-border appearance-none overflow-hidden" type="date" value={form.date} onChange={(event) => update('date', event.target.value)} />
              </div>
            </div>
            <div className="field">
              <label>Status</label>
              <select className="sketch-input" value={form.status} onChange={(event) => update('status', event.target.value)}>
                {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="row2">
            <div className="field">
              <label>Salary Range</label>
              <input className="sketch-input" value={form.salary} onChange={(event) => update('salary', event.target.value)} onBlur={(event) => update('salary', formatSalaryValue(event.target.value))} placeholder="e.g. $150k – $180k" />
            </div>
            <div className="field">
              <label>Contact name</label>
              <input className="sketch-input" value={form.contactName} onChange={(event) => update('contactName', event.target.value)} placeholder="Recruiter, hiring manager, or referral name" />
            </div>
          </div>

          <div className="row2">
            <div className="field">
              <label>Contact email</label>
              <input className="sketch-input" type="email" value={form.contactEmail} onChange={(event) => update('contactEmail', event.target.value)} placeholder="recruiter@company.com" />
            </div>
            <div className="field">
              <label>Contact LinkedIn</label>
              <input className="sketch-input" value={form.contactLinkedIn} onChange={(event) => update('contactLinkedIn', event.target.value)} placeholder="linkedin.com/in/…" />
            </div>
          </div>

          <div className="row2">
            <div className="field">
              <label>Sent email?</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 2 }}>
                <input
                  type="checkbox"
                  checked={form.sentEmail}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setForm(curr => ({ ...curr, sentEmail: checked, sentEmailDate: checked ? todayISO() : '' }));
                  }}
                  style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                />
                <span style={{ fontSize: 14 }}>Email sent</span>
              </label>
            </div>
            <div className="field modal-date-field">
              <label>Sent email date</label>
              <div className="modal-date-clip w-full max-w-full min-w-0 overflow-hidden">
                <input className="sketch-input modal-date-input w-full max-w-full min-w-0 box-border appearance-none overflow-hidden" type="date" value={form.sentEmailDate} onChange={(event) => update('sentEmailDate', event.target.value)} />
              </div>
            </div>
          </div>

          <div className="field">
            <label>Listing link</label>
            <input
              ref={linkInputRef}
              className="sketch-input"
              type="text"
              value={form.link}
              onChange={(event) => update('link', event.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData('text').trim();
                if (pasted.startsWith('http')) setTimeout(() => triggerAutofill(pasted), 0);
              }}
              placeholder="Paste a direct job posting URL to autofill details"
            />
            {autofillStatus === 'loading' && (
              <p className="autofill-hint autofill-loading">Checking job link…</p>
            )}
            {autofillStatus === 'done' && (
              <p className="autofill-hint autofill-ok">Fields filled from job listing.</p>
            )}
            {autofillStatus === 'partial' && (
              <p className="autofill-hint autofill-warn">Found title and salary. Company name wasn't detected.</p>
            )}
            {autofillStatus === 'partial-notitle' && (
              <p className="autofill-hint autofill-warn">Filled some details. Job title wasn't detected — add it manually.</p>
            )}
            {autofillStatus === 'cors' && (
              <p className="autofill-hint autofill-warn">Couldn't read this page automatically. You can still fill it in manually.</p>
            )}
            {autofillStatus === 'linkedin' && (
              <p className="autofill-hint autofill-warn">Couldn't read this page automatically. You can still fill it in manually.</p>
            )}
            {!autofillStatus && (
              <p className="autofill-hint">Use the company's direct job posting when possible. LinkedIn and Easy Apply pages are not supported for autofill.</p>
            )}
          </div>

          <div className="field">
            <label>Notes</label>
            <textarea className="sketch-input" rows={1} style={{ resize: 'vertical' }} value={form.notes} onChange={(event) => update('notes', event.target.value)} placeholder="Anything useful to remember about this one." />
          </div>

        </form>
        <div className="add-modal-actions">
          <label className="favorite-toggle" style={{ margin: 0 }}>
            <input
              type="checkbox"
              form="add-job-form"
              checked={form.favorite}
              onChange={(event) => update('favorite', event.target.checked)}
            />
            <span className="material-icons favorite-heart">auto_awesome</span>
            <span>Add to favorites</span>
          </label>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="sketch-btn add-modal-cancel" type="button" onClick={onClose}>Cancel</button>
            <button className="candy-btn add add-modal-submit" type="submit" form="add-job-form">Add job</button>
          </div>
        </div>
      </section>
    </div>
  );
}

function UpgradeModal({ message, onClose }) {
  return (
    <div className="scrim upgrade-scrim" onClick={onClose}>
      <section className="sketch-box upgrade-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="upgrade-title">
        <div className="upgrade-modal-head">
          <div>
            <h2 id="upgrade-title">Upgrade to Pro</h2>
            <p className="hint">{message}</p>
          </div>
          <button className="add-modal-close-x" type="button" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <p className="upgrade-copy">Unlock CSV import, CSV export, and bulk editing when you are ready.</p>

        <div className="form-actions">
          <button className="candy-btn add" type="button" onClick={() => { window.location.href = 'pricing.html'; }}>View plans</button>
        </div>
      </section>
    </div>
  );
}

function CsvLimitModal({ title, message, onClose }) {
  return (
    <div className="scrim upgrade-scrim" onClick={onClose}>
      <section className="sketch-box upgrade-modal csv-limit-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="csv-limit-title">
        <div className="upgrade-modal-head">
          <div>
            <h2 id="csv-limit-title">{title}</h2>
            <p className="hint">{message}</p>
          </div>
        </div>

        <div className="form-actions">
          <button className="candy-btn add" type="button" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
